import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { type ChangeEvent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch } from '../../../../../configuration/setup/hooks';
import { type EulaStatusUpdateRequest, type EulaUpdateStatus, eulaFormField } from '../../../../app/types';
import { useUpdateManEulaStatusMutation } from '../../../../services/accountsApi';
import type { ManEulaStatus } from '../../../../services/schemas/accountsBackendCodec';
import { FormInputField } from '../FormInputField';
import { emailSchema, nameSchema, reasonSchema, versionSchema } from '../schema';
import { resetAccountsSidebar } from '../sidebar/accountSidebarSlice';

type FormErrors = { [key: string]: string | undefined };

export interface EulaStatusDialogConfig {
    buttonText: string;
    confirmationText: string;
    confirmButtonText: string;
    dialogTitle: string;
    errorLogVerb: string;
    errorMessageId: string;
    isButtonDisabled: (status: ManEulaStatus['status']) => boolean;
    successMessageId: string;
    targetStatus: EulaUpdateStatus;
}

interface EulaStatusDialogProps {
    config: EulaStatusDialogConfig;
    eulaStatus: ManEulaStatus;
}

const getFormErrors = (request: EulaStatusUpdateRequest, eulaVersion: string): FormErrors => ({
    [eulaFormField.FIRST_NAME]:
        request.firstname !== undefined && nameSchema.validate(request.firstname).error
            ? 'helpgang.addFleetAdminDialog.name.error'
            : undefined,
    [eulaFormField.LAST_NAME]:
        request.lastname !== undefined && nameSchema.validate(request.lastname).error
            ? 'helpgang.addFleetAdminDialog.name.error'
            : undefined,
    [eulaFormField.EMAIL]:
        request.email !== undefined && emailSchema.validate(request.email).error
            ? 'helpgang.addFleetAdminDialog.email.error'
            : undefined,
    [eulaFormField.VERSION]:
        eulaVersion !== '' && versionSchema.validate(eulaVersion).error
            ? 'helpgang.eulaStatusDialog.version.error'
            : undefined,
    [eulaFormField.REASON]:
        request.reason !== undefined && reasonSchema.validate(request.reason).error
            ? 'helpgang.addFleetAdminDialog.reason.error'
            : undefined,
});

const isNotEmpty = (value: string | null | undefined) => value != null && value.trim().length !== 0;
const getInitialEulaVersion = (eulaStatus: ManEulaStatus) => eulaStatus.eula_version ?? '';

export const EulaStatusDialog = ({ config, eulaStatus }: EulaStatusDialogProps) => {
    const [showDialog, setShowDialog] = useState(false);
    const [confirmationInput, setConfirmationInput] = useState('');
    const [request, setRequest] = useState({} as EulaStatusUpdateRequest);
    const [eulaVersion, setEulaVersion] = useState(getInitialEulaVersion(eulaStatus));

    const formErrors = getFormErrors(request, eulaVersion);
    const hasFormErrors = Object.values(formErrors).some(error => error !== undefined);

    const dispatch = useAppDispatch();
    const intl = useIntl();

    const [
        updateManEulaStatus,
        {
            isLoading: updateManEulaStatusIsLoading,
            isSuccess: updateManEulaStatusIsSuccess,
            isError: updateManEulaStatusIsError,
            error: updateManEulaStatusError,
            reset: resetMutationState,
        },
    ] = useUpdateManEulaStatusMutation();

    useEffect(() => {
        if (updateManEulaStatusIsSuccess) {
            Notification.success(intl.formatMessage({ id: config.successMessageId }));
            dispatch(resetAccountsSidebar());
            onClose();
        }
    }, [config.successMessageId, dispatch, intl, updateManEulaStatusIsSuccess]);

    useEffect(() => {
        if (updateManEulaStatusIsError) {
            console.error(`Could not ${config.errorLogVerb} EULA: ${JSON.stringify(updateManEulaStatusError)}`);
            Notification.error(intl.formatMessage({ id: config.errorMessageId }));
        }
    }, [config.errorLogVerb, config.errorMessageId, intl, updateManEulaStatusError, updateManEulaStatusIsError]);

    const resetState = () => {
        setConfirmationInput('');
        setRequest({} as EulaStatusUpdateRequest);
        setEulaVersion(getInitialEulaVersion(eulaStatus));
        resetMutationState();
    };

    const onClose = () => {
        resetState();
        setShowDialog(false);
    };

    const isConfirmationTextValid = () => config.confirmationText === confirmationInput;

    const isFormValid = () =>
        isConfirmationTextValid() &&
        isNotEmpty(request.firstname) &&
        isNotEmpty(request.lastname) &&
        isNotEmpty(request.email) &&
        isNotEmpty(eulaVersion) &&
        isNotEmpty(request.reason) &&
        !hasFormErrors;

    const handlePropertyChange = (propertyName: string, value: string | boolean) => {
        if (typeof value !== 'string') {
            return;
        }
        setRequest(prevState => ({
            ...prevState,
            [propertyName]: value,
        }));
    };

    const handleConfirmationInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setConfirmationInput(event.target.value);
    };

    const handleEulaVersionChange = (_propertyName: string, value: string) => {
        setEulaVersion(value);
    };

    const handleConfirmButtonClick = () => {
        if (!isFormValid()) {
            return;
        }

        updateManEulaStatus({
            req: request,
            eulaStatus: {
                ...eulaStatus,
                status: config.targetStatus,
                eula_version: eulaVersion,
            },
        });
    };

    return (
        <>
            <button
                type='button'
                className='btn btn-warning btn-outline'
                disabled={config.isButtonDisabled(eulaStatus.status)}
                onClick={() => {
                    resetState();
                    setShowDialog(true);
                }}
            >
                {config.buttonText}
            </button>

            <ConfirmationDialog
                show={showDialog}
                title={config.dialogTitle}
                content={
                    <div data-cy={'modal-content-change-lifecycle-state'}>
                        <div>
                            <FormInputField
                                dataAttribute='eula-status-dialog-first-name-input'
                                translationId={'helpgang.addFleetAdminDialog.firstName'}
                                value={request.firstname}
                                propertyName={eulaFormField.FIRST_NAME}
                                formError={formErrors[eulaFormField.FIRST_NAME]}
                                autoFocus={true}
                                required={true}
                                onValueChange={handlePropertyChange}
                            />
                            <FormInputField
                                dataAttribute='eula-status-dialog-last-name-input'
                                translationId={'helpgang.addFleetAdminDialog.lastName'}
                                value={request.lastname}
                                propertyName={eulaFormField.LAST_NAME}
                                formError={formErrors[eulaFormField.LAST_NAME]}
                                required={true}
                                onValueChange={handlePropertyChange}
                            />
                            <FormInputField
                                dataAttribute='eula-status-dialog-email-input'
                                translationId={'helpgang.addFleetAdminDialog.email'}
                                placeholder={'max.mustermann@example.com'}
                                value={request.email}
                                propertyName={eulaFormField.EMAIL}
                                formError={formErrors[eulaFormField.EMAIL]}
                                required={true}
                                onValueChange={handlePropertyChange}
                            />
                            <FormInputField
                                dataAttribute='eula-status-dialog-version-input'
                                translationId={'helpgang.eulaStatusDialog.version'}
                                value={eulaVersion}
                                propertyName={eulaFormField.VERSION}
                                formError={formErrors[eulaFormField.VERSION]}
                                required={true}
                                onValueChange={handleEulaVersionChange}
                            />
                            <FormInputField
                                dataAttribute='eula-status-dialog-reason-input'
                                translationId={'helpgang.addFleetAdminDialog.reason'}
                                value={request.reason}
                                propertyName={eulaFormField.REASON}
                                formError={formErrors[eulaFormField.REASON]}
                                required={true}
                                onValueChange={handlePropertyChange}
                                id={'reasonInputField'}
                            />
                        </div>
                        <p>
                            <FormattedMessage id={'helpgang.modal.typeConfirmation'} />:{' '}
                            <span className='text-bold text-color-danger pointer-events-none user-select-none'>
                                {config.confirmationText}
                            </span>
                        </p>
                        <div className='form-group'>
                            <input
                                className='form-control'
                                type='text'
                                value={confirmationInput}
                                placeholder='Confirmation Input'
                                onChange={handleConfirmationInputChange}
                                data-cy={'update-account-locking-info-dialog-confirmation-input'}
                            />
                        </div>
                    </div>
                }
                bsSize={'md'}
                onClickConfirm={handleConfirmButtonClick}
                onClickCancel={onClose}
                cancelButtonText={<FormattedMessage id={'helpgang.confirmationDialog.cancel'} />}
                confirmButtonText={config.confirmButtonText}
                useOverflow={true}
                disableConfirm={updateManEulaStatusIsLoading || !isFormValid()}
            />
        </>
    );
};
